import React, { useMemo, useState } from 'react';
import { Bot, LoaderCircle, ShieldCheck, SlidersHorizontal, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { formatAddonPrice, getAiDashboardAddonPricing, isAiDashboardAddonEnabled } from '../lib/aiDashboardAddons';

const PANEL_CLASS = 'rounded-3xl border border-border bg-card text-foreground shadow-sm';
const SUB_PANEL_CLASS = 'rounded-2xl border border-border bg-muted';

const CAPABILITY_ITEMS = [
  {
    key: 'nlp',
    title: 'Natural Language Processing',
    description: 'Enable AI-powered text analysis and semantic understanding.',
  },
  {
    key: 'vision',
    title: 'Computer Vision',
    description: 'Image recognition and visual content analysis for AI workflows.',
  },
  {
    key: 'generation',
    title: 'Content Generation',
    description: 'Automated content and response generation through managed prompts.',
  },
  {
    key: 'forecasting',
    title: 'Predictive Analytics',
    description: 'Data forecasting, trend prediction, and proactive anomaly detection.',
  },
  {
    key: 'recommendations',
    title: 'Recommendation Engine',
    description: 'Personalized recommendations for HR workflows, teams, and users.',
  },
];

const LockedAddonState = ({ enabled, addonKey }) => {
  const { user } = useAuth();
  const pricing = getAiDashboardAddonPricing(user?.limits || {}, addonKey);

  if (enabled) return null;

  return (
    <Card className="rounded-3xl border border-warning/30 bg-warning/10 shadow-sm">
      <CardHeader>
        <CardTitle className="text-foreground">Plan Add-on Required</CardTitle>
        <CardDescription className="text-muted-foreground">
          AI Settings is isolated from AI Dashboard and unlocks through the selected subscription plan.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Additional monthly uplift: <span className="font-semibold text-foreground">{formatAddonPrice(pricing, 'monthly')}</span></p>
          <p className="text-sm text-muted-foreground">Additional yearly uplift: <span className="font-semibold text-foreground">{formatAddonPrice(pricing, 'yearly')}</span></p>
        </div>
        <div className="rounded-2xl border border-warning/30 bg-card px-4 py-3 text-sm text-muted-foreground">
          Current plan: <span className="font-medium text-foreground">{user?.subscriptionPlanName || user?.subscriptionPlanCode || 'Unassigned'}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const AICenterSettingsPage = ({ scope = 'tenant' }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    nlp: true,
    vision: false,
    generation: true,
    forecasting: true,
    recommendations: true,
    primaryModel: scope === 'superadmin' ? 'gpt-4.1' : 'gpt-4o-mini',
    responseStyle: 'balanced',
    confidenceThreshold: '78',
    retrainingCadence: 'weekly',
    aiPermissions: 'admins_only',
    piiMasking: true,
    auditLogging: true,
  });

  const enabled = isAiDashboardAddonEnabled(user?.limits || {}, 'ai_settings', scope === 'superadmin' || user?.isSuperadmin);
  const enabledCapabilities = useMemo(
    () => CAPABILITY_ITEMS.filter((item) => settings[item.key]).length,
    [settings]
  );

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSaving(false);
    toast.success('AI Settings saved');
  };

  return (
    <div className="space-y-6">
      <section className="group relative overflow-hidden rounded-[28px] border border-border bg-gradient-to-br from-primary/10 via-card to-info/10 p-6 shadow-[0_22px_60px_rgba(15,23,42,0.08)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
          <div className="absolute right-0 bottom-0 h-44 w-44 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
        </div>
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
              <Bot className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
              {scope === 'superadmin' ? 'Superadmin AI Center' : 'Tenant AI Center'}
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">AI Settings</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
                Dedicated AI configuration for model behavior, training policy, capability toggles, and AI permissions. This page is isolated from AI Dashboard and can be extended with backend persistence later.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Capabilities</p>
                <p className="mt-3 text-2xl font-semibold text-foreground">{enabledCapabilities}/5</p>
              </div>
              <div className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Primary Model</p>
                <p className="mt-3 text-2xl font-semibold text-foreground">{settings.primaryModel}</p>
              </div>
              <div className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Updated</p>
                <p className="mt-3 text-2xl font-semibold text-foreground">15 May 2026</p>
              </div>
            </div>
          </div>
          <div className="grid gap-4">
            <div className="rounded-2xl border border-border bg-card/92 p-5 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">AI Configuration</p>
                  <p className="mt-2 text-lg font-semibold text-foreground">Reference-inspired control surface for capabilities, models, and permissions.</p>
                </div>
                <Sparkles className="h-5 w-5 text-primary transition-transform duration-300 group-hover:scale-110" />
              </div>
            </div>
            <div className="flex gap-3 xl:justify-end">
              <Button variant="outline" className="border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/10 hover:text-primary" onClick={() => toast.success('AI configuration exported')}>
                Export
              </Button>
              <Button className="bg-primary text-primary-foreground shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-hover" onClick={() => toast.success('AI scan started')}>
                Run AI Scan
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LockedAddonState enabled={enabled} addonKey="ai_settings" />

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Wand2 className="h-5 w-5 text-primary" />
              AI Configuration
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Capability toggles inspired by the reference AI settings pattern.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {CAPABILITY_ITEMS.map((item) => (
              <div key={item.key} className={`${SUB_PANEL_CLASS} flex items-center justify-between gap-4 p-4`}>
                <div>
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                </div>
                <Switch checked={settings[item.key]} onCheckedChange={(checked) => updateSetting(item.key, checked)} />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="grid gap-6">
          <Card className={PANEL_CLASS}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <SlidersHorizontal className="h-5 w-5 text-primary" />
                AI Model Settings
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Tune model choice, response style, and confidence controls.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Primary Model</Label>
                <Select value={settings.primaryModel} onValueChange={(value) => updateSetting('primaryModel', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Response Style</Label>
                <Select value={settings.responseStyle} onValueChange={(value) => updateSetting('responseStyle', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="balanced">Balanced</SelectItem>
                    <SelectItem value="strict">Strict</SelectItem>
                    <SelectItem value="creative">Creative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Confidence Threshold</Label>
                <Input value={settings.confidenceThreshold} onChange={(event) => updateSetting('confidenceThreshold', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Retraining Cadence</Label>
                <Select value={settings.retrainingCadence} onValueChange={(value) => updateSetting('retrainingCadence', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className={PANEL_CLASS}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <ShieldCheck className="h-5 w-5 text-primary" />
                AI Permissions
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Define who can use AI features and how sensitive data is handled.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="space-y-2">
                <Label>AI Access</Label>
                <Select value={settings.aiPermissions} onValueChange={(value) => updateSetting('aiPermissions', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admins_only">Admins Only</SelectItem>
                    <SelectItem value="managers_and_admins">Managers and Admins</SelectItem>
                    <SelectItem value="all_authorized_users">All Authorized Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={`${SUB_PANEL_CLASS} flex items-center justify-between gap-4 p-4`}>
                <div>
                  <p className="text-sm font-semibold text-foreground">PII Masking</p>
                  <p className="mt-1 text-sm text-muted-foreground">Mask personal data before AI processing and prompt orchestration.</p>
                </div>
                <Switch checked={settings.piiMasking} onCheckedChange={(checked) => updateSetting('piiMasking', checked)} />
              </div>
              <div className={`${SUB_PANEL_CLASS} flex items-center justify-between gap-4 p-4`}>
                <div>
                  <p className="text-sm font-semibold text-foreground">Audit Logging</p>
                  <p className="mt-1 text-sm text-muted-foreground">Track prompt execution, AI actions, and model-side configuration changes.</p>
                </div>
                <Switch checked={settings.auditLogging} onCheckedChange={(checked) => updateSetting('auditLogging', checked)} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={() => toast.success('AI settings reset')}>
          Cancel
        </Button>
        <Button className="bg-primary text-primary-foreground hover:bg-primary-hover" onClick={handleSave} disabled={saving}>
          {saving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AICenterSettingsPage;
